import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import WorkerBottomNav from '../../components/worker/WorkerBottomNav';
import WorkerRateConsumerModal from '../../components/worker/WorkerRateConsumerModal';
import '../../styles/worker-dashboard.css';

export default function WorkHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingJob, setRatingJob] = useState(null);

  useEffect(() => {
    let unsubSnap = null;
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (unsubSnap) {
        unsubSnap();
        unsubSnap = null;
      }
      if (!u) {
        setLoading(false);
        return;
      }
      const q = query(
        collection(db, 'bookings'),
        where('workerId', '==', u.uid),
        where('status', '==', 'completed')
      );
      unsubSnap = onSnapshot(q, (snap) => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setHistory(data);
        setLoading(false);
      }, () => {
        setHistory([]);
        setLoading(false);
      });
    });
    return () => {
      unsubAuth();
      if (unsubSnap) unsubSnap();
    };
  }, []);

  const totalEarned = history.reduce((s, h) => s + Number(h.earned || h.workerReceivable || h.settlement?.workerEarnings || 0), 0);

  // Finalized average rating logic:
  // Ratings from the current week (Mon-Sun) are not yet included in the average.
  // They are averaged on Sunday and reflected from the following Monday.
  // Non-rated works are ignored in the calculation.
  const parseDate = (d) => d && typeof d.toDate === 'function' ? d.toDate() : new Date(d);

  const computeAvgRating = (items) => {
    if (items.length === 0) return 0;
    return (items.reduce((s, h) => s + h.rating, 0) / items.length).toFixed(1);
  };

  const getFinalizedAvgRating = (items) => {
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
    // Find the most recent Monday (start of the display week)
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
    monday.setHours(0, 0, 0, 0);

    // Only include rated items completed before this Monday (finalized weeks)
    const finalizedItems = items.filter(h => {
      if (!h.rating || h.reviewsReleased === false) return false;
      return parseDate(h.completedAt) < monday;
    });

    return computeAvgRating(finalizedItems);
  };

  // Display only finalized ratings (current week excluded until next Monday)
  const avgRating = getFinalizedAvgRating(history);

  return (
    <div className="worker-page">
      <div className="worker-container">
        <div className="worker-page-topbar">
          <Link to="/worker/dashboard" className="worker-back-link" aria-label="Back to worker dashboard">←</Link>
          <h2 className="worker-page-title">Work History</h2>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Completed', value: history.length, icon: '✅' },
            { label: 'Earned', value: `₹${totalEarned}`, icon: '💰' },
            { label: 'Avg Rating', value: `${avgRating}★`, icon: '⭐' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <span style={{ fontSize: 20 }}>{s.icon}</span>
              <span className="stat-value" style={{ fontSize: 18 }}>{s.value}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        {loading ? (
          [1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 120, borderRadius: 14, marginBottom: 12 }} />)
        ) : history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No history yet</h3>
            <p>Completed jobs will appear here.</p>
          </div>
        ) : (
          history.map(item => (
            <div key={item.id} className="worker-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1F1144' }}>{item.title || item.serviceType}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{item.category || item.serviceType} · {item.customer || item.customerName}</div>
                </div>
                {item.earned && (
                  <div style={{ background: '#D1FAE5', color: '#065F46', padding: '4px 10px', borderRadius: 8, fontWeight: 700, fontSize: 14 }}>
                    +₹{item.earned}
                  </div>
                )}
              </div>
              {item.reviewsReleased !== false && (item.review || item.reviewText) && (
                <div style={{ fontSize: 13, color: '#6B7280', fontStyle: 'italic', marginBottom: 6 }}>"{item.review || item.reviewText}"</div>
              )}
              {item.reviewsReleased !== false && item.rating && (
                <div style={{ fontSize: 12, color: '#ca8a04', marginBottom: 6 }}>
                  {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                </div>
              )}
              {!item.workerRated && item.id && !item.id.startsWith('h') && (
                <button
                  className="btn-primary"
                  style={{ padding: '6px 12px', fontSize: '12px', width: 'auto', marginTop: '8px' }}
                  onClick={() => setRatingJob(item)}
                >
                  ⭐ Rate Customer
                </button>
              )}
              {item.workerRated && !item.reviewsReleased && (
                <span style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic', display: 'block', marginTop: 8 }}>
                  Feedback submitted (pending customer feedback)
                </span>
              )}
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>
                📅 {item.completedAt ? (item.completedAt.seconds ? new Date(item.completedAt.seconds * 1000).toLocaleDateString() : item.completedAt.toString()) : 'N/A'}
              </div>
            </div>
          ))
        )}
      </div>

      {ratingJob && (
        <WorkerRateConsumerModal
          job={ratingJob}
          onClose={() => setRatingJob(null)}
          onCompleted={() => {
            setRatingJob(null);
          }}
        />
      )}

      <WorkerBottomNav />
    </div>
  );
}
